<template>
    <layout-internal-container :key="sidebarKey">
        <div class="flex flex-row gap-4 w-full border px-5 py-4 items-center">
            <a-avatar v-if="form.photo"
                :src="form.photo.includes('https') ? form.photo + '?token=' + useAuth().get().token : form.photo"
                :size="60"></a-avatar>
            <div class="flex flex-row gap-1 justify-start" v-else>
                <a-avatar :size="60" class="bg-primary">{{ getLetters(fullName) }}</a-avatar>
                <!-- <span class="text-red-500 self-start" v-if="!form.photo">*</span> -->
            </div>
            <div class="flex flex-row gap-1">
                <div @click="triggerFileUpload"
                    class="flex flex-row gap-2 items-center border-primary border px-3 py-1 rounded-lg text-primary cursor-pointer">
                    <IconUpload :size="16" />
                    <span class="text-sm">Upload New Picture</span>
                </div>
                <input type="file" ref="fileInput" @change="handleFileChange" accept="image/*" class="hidden" />
                <span v-if="errors.photo" class="text-red-500 text-xs tracking-wide mt-2 ms-2">{{ errors.photo }}</span>
                
            </div>
        </div>

        <a-alert
            message="Please fill in your gender information and make sure to click Save before continuing!"
            type="warning"
            show-icon
            class="font-medium"
            v-if="!form.gender && isMounted"
        >
            <template #icon><IconExclamationCircleFilled /></template>
        </a-alert>

        <div class="flex flex-col gap-4 border w-full px-5 pt-5 pb-8">
            <div class="grid md:grid-cols-1 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Email</span>
                    <a-input v-model:value="form.email" size="large" disabled />
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Username <span class="text-red-500">*</span></span>
                    <a-input v-model:value="form.username" size="large" prefix="@" />
                    <span v-if="errors.username" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.username }}</span>
                </div>
                
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Phone</span>

                    <div class="flex flex-row items-center gap-4">
                        <a-input :value="'+'+form.phone" size="large" class="w-12/12" disabled />
                        <!-- <span v-if="form.isPhoneVerified" class="text-primary text-sm font-normal w-5/12 flex items-center gap-2">
                            <IconRosetteDiscountCheckFilled /> Phone Number Verified
                        </span> -->

                        <!-- <span v-if="!form.isPhoneVerified && !isVerifyPhoneNumber" @click="onResend" class="cursor-pointer text-sm font-semibold w-8/12" :class="isVerifyPhoneNumber ? 'text-secondary' : 'text-primary'">Verify phone number</span> -->

                        <div class="flex flex-row items-center gap-4" v-if="isVerifyPhoneNumber && !form.isPhoneVerified">
                            <a-input v-model:value="otp" size="large" class="w-4/12" placeholder="Input Code"/>

                            <a-button :loading="isLoadingVerify" @click="onVerify" class="w-3/12" type="primary" shape="round" size="medium">
                                Verify
                            </a-button>
                            
                            <span @click="onResend" class="text-primary font-medium w-5/12" 
                                :class="{'cursor-not-allowed': isTimerDisabled, 'cursor-pointer': !isTimerDisabled}" :style="{ opacity: isTimerDisabled ? 0.5 : 1 }">
                                {{ isTimerDisabled ? `Resend in ${formattedTime}` : (isLoadingResend ? 'Please Wait...' : 'Resend') }}
                            </span>
                        </div>
                    </div>
                    
                    <span class="text-sm text-secondary" v-if="isVerifyPhoneNumber">We have just sent a 6-digit verification code to you via WhatsApp. If you do not receive the code, please press Resend within 5 minutes.</span>
                </div>

            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Full name <span class="text-red-500">*</span></span>
                    <a-input size="large" v-model:value="form.name" />
                    <span v-if="errors.name" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.name }}</span>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Company Name <span class="text-red-500">*</span></span>
                    <a-input v-model:value="form.company_name" size="large" />
                    <span v-if="errors.company_name" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.company_name }}</span>
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Position <span class="text-red-500">*</span></span>
                    <a-input size="large" v-model:value="form.position" placeholder="e.g CEO, CMO, CTO or Chairman" />
                    <span v-if="errors.position" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.position }}</span>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Company Sector <span class="text-red-500">*</span></span>
                    <a-select size="large" v-model:value="form.company_sector" placeholder="- Select Company Sector -">
                        <a-select-option value="Sustainable Agriculture/Raw Materials">Sustainable Agriculture/Raw Materials</a-select-option>
                        <a-select-option value="Fisheries & Aquaculture">Fisheries & Aquaculture</a-select-option>
                        <a-select-option value="Circular Economy & Climate Resilience">Circular Economy & Climate Resilience</a-select-option>
                        <a-select-option value="Architecture & Interior Design">Architecture & Interior Design</a-select-option>
                        <a-select-option value="Banking, Financial Services & Insurance">Banking, Financial Services & Insurance</a-select-option>
                        <a-select-option value="Beauty & Skin Care">Beauty & Skin Care</a-select-option>
                        <a-select-option value="Creative Arts, Crafts, Fashion & Culture">Creative Arts, Crafts, Fashion & Culture</a-select-option>
                        <a-select-option value="Education">Education</a-select-option>
                        <a-select-option value="Employment">Employment</a-select-option>
                        <a-select-option value="Environment & Sustainable Energy">Environment & Sustainable Energy</a-select-option>
                        <a-select-option value="Food & Beverage (F&B)">Food & Beverage (F&B)</a-select-option>
                        <a-select-option value="Retail & E-commerce">Retail & E-commerce</a-select-option>
                        <a-select-option value="Health & Medicine">Health & Medicine</a-select-option>
                        <a-select-option value="Logistics & Transportation">Logistics & Transportation</a-select-option>
                        <a-select-option value="Manufacturing & Infrastructure">Manufacturing & Infrastructure</a-select-option>
                        <a-select-option value="Telecommunications & Digital">Telecommunications & Digital</a-select-option>
                        <a-select-option value="Tourism">Tourism</a-select-option>
                        <a-select-option value="Other">Other</a-select-option>
                    </a-select>
                    <span v-if="errors.company_sector" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.company_sector }}</span>
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Gender <span class="text-red-500">*</span></span>
                    <a-select size="large" v-model:value="form.gender" placeholder="- Select -">
                        <a-select-option value="M">Male</a-select-option>
                        <a-select-option value="F">Female</a-select-option>
                        <a-select-option value="NB">Non Binary</a-select-option>
                    </a-select>
                    <span v-if="!form.gender && isMounted" class="text-red-500 text-xs tracking-wide mt-2">The gender field is required.</span>
                    <span v-if="errors.gender" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.gender }}</span>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Designation <span class="text-red-500">*</span></span>
                    <a-select size="large" v-model:value="form.designation" placeholder="- Select -">
                        <a-select-option value="Mr">Mr</a-select-option>
                        <a-select-option value="Ms">Ms</a-select-option>
                        <a-select-option value="Mrs">Mrs</a-select-option>
                        <a-select-option value="Dr">Dr</a-select-option>
                        <a-select-option value="Prof">Prof</a-select-option>
                        <a-select-option value="Others">Others </a-select-option>
                    </a-select>
                    <span v-if="errors.designation" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.designation }}</span>
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Year of Establishment <span class="text-red-500">*</span></span>
                    
                    <a-select size="large" class="mt-2 w-full" :name="'year'" :placeholder="'-- Choose Year --'" required v-model:value="form.year_estbl">
                        <a-select-option v-for="option in yearOptions" :key="option" :value="option">{{ option }}</a-select-option>
                    </a-select>

                    <span v-if="errors.year_estbl" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.year_estbl }}</span>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Website or Social Media <span class="text-red-500">*</span></span>
                    <a-input size="large" v-model:value="form.professional_link" placeholder="https://yourwebsite.com" />
                    <span v-if="errors.professional_link" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.professional_link }}</span>
                </div>
            </div>
            <div class="grid md:grid-cols-2 gap-6">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Province <span
                        class="text-red-500">*</span></span>
                            
                    <a-select size="large" class="mt-2 w-full" :name="'province'" :placeholder="'-- Choose Province --'" required v-model:value="form.province_id">
                        <a-select-option v-for="option in provinceOptions" :key="option.id" :value="option.id">{{ option.name }}</a-select-option>
                    </a-select>

                    <span v-if="errors.province_id" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.province_id }}</span>
                </div>

                
                <div class="w-full flex-col justify-start items-start">
                    <span class="text-sm text-secondary tracking-wide">Beneficiaries Type <span
                        class="text-red-500">*</span></span>
                    
                    <a-select size="large" class="mt-2 w-full" :name="'beneficaries_type'" :placeholder="'-- Choose Beneficiaries --'" required v-model:value="form.beneficiaries_type">
                        <a-select-option v-for="option in beneficiaryOptions" :key="option" :value="option">{{ option }}</a-select-option>
                    </a-select>
                    <span v-if="errors.beneficiaries_type" class="text-red-500 text-xs tracking-wide mt-2">{{
                        errors.beneficiaries_type }}</span>
                </div>
            </div>

            <div class="grid md:grid-cols-2 w-full gap-6 mt-2">
                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Description of Your Business<span class="text-red-500">*</span></span>
                    <a-textarea show-count :maxlength="1000" v-model:value="form.description" class="resize-none"
                        :rows="4" />

                    <span v-if="errors.description" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.description }}</span>
                </div>

                <div class="flex flex-col gap-2">
                    <span class="text-sm text-secondary tracking-wide">Impact Measurement</span>
                    <a-textarea show-count :maxlength="150" v-model:value="form.impact_measurement" class="resize-none"
                        :rows="4" />
                    <span v-if="errors.impact_measurement" class="text-red-500 text-xs tracking-wide mt-2">{{
                        errors.impact_measurement }}</span>
                </div>
            </div>

            <div class="grid md:grid-cols-2 w-full gap-3 my-3">
                <span class="text-sm text-secondary tracking-wide">Funding</span>

                <div
                    v-for="(funding, index) in form.funding_history"
                    :key="funding.id"
                    class="col-span-2 grid grid-cols-10 items-center justify-center gap-3"
                >
                    <hr class="col-span-10 mb-2" v-if="index > 0">
                    
                    <div class="col-span-9 grid grid-cols-9 items-center justify-center gap-3">
                        <a-select size="large" class="col-span-3 md:col-span-2 md:order-1" :name="'Year'" :placeholder="'-- Year --'" required v-model:value="funding.year">
                            <a-select-option v-for="option in yearOptions" :key="option" :value="option">{{ option }}</a-select-option>
                        </a-select>

                        <a-input-number
                            size="large" bordered class="w-full col-span-6 md:col-span-3 md:order-3"
                            placeholder="Amount"
                            v-model:value="funding.amount"
                            :formatter="value => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')"
                            :parser="value => value.replace(/Rp\s?|(,*)/g, '')"
                        />

                        <a-input v-model:value="funding.investor" size="large" placeholder="Investor Name" class="col-span-9 md:col-span-4 md:order-2" />
                    </div>
                    
                    <IconMinus @click="removeFunding(funding)" class="cursor-pointer col-span-1 flex items-center justify-center self-center" />

                    <div class="col-span-10 flex flex-col">
                        <span v-if="errors?.funding_history[index]" class="text-red-500 text-xs tracking-wide mt-2">{{ errors?.funding_history[index] }}</span>
                    </div>
                </div>

                <a-button type="dashed" block @click="addFunding" class="flex flex-row items-center justify-center gap-1 col-span-2">
                    <IconPlus />
                    Add funding
                </a-button>
            </div>

            <div class="grid md:grid-cols-2 w-full gap-6">
                <div class="gap-2 col-span-2">
                    <span class="text-sm text-secondary tracking-wide">How did you first hear about this platform/program?</span>
                    
                    <div>
                        <span v-if="errors.referral_source" class="text-red-500 text-xs tracking-wide mt-2">{{
                            errors.referral_source }}</span>
                    </div>

                    <!-- <a-checkbox-group v-model:value="form.referral_source" class="grid grid-cols-1 gap-3 w-full mt-2 items-center">
                        <a-checkbox v-for="program in programs" :key="program.id" :value="program.title"  class="break-words whitespace-normal">{{ program.title }}</a-checkbox>
                    </a-checkbox-group> -->
                    <a-radio-group v-model:value="form.referral_source" class="flex flex-col gap-3 mt-2 w-full">
                        <a-radio :value="'Social media (Instagram, LinkedIn, Facebook, YouTube, etc.)'">Social media (Instagram, LinkedIn, Facebook, YouTube, etc.)</a-radio>
                        <a-radio :value="'Instellar website'">Instellar website</a-radio>
                        <a-radio :value="'Instellar email newsletter'">Instellar email newsletter</a-radio>
                        <a-radio :value="'Friend/colleague'">Friend/colleague</a-radio>
                        <a-radio :value="'Other (write your own)'">
                            Other (write your own)
                        </a-radio>
                    </a-radio-group>

                    <a-input v-if="form.referral_source === 'Other (write your own)'" v-model:value="form.other_hear" class="w-6/12 ms-6 mt-2" />
                </div>
            </div>

            <div class="grid md:grid-cols-2 gap-6">
                <div class="gap-2 col-span-2">
                    <span class="text-sm text-secondary tracking-wide">Industry  <span class="text-xs">(max. 3)</span><span class="text-red-500">*</span></span>
                    <div>
                        <span v-if="errors.industry" class="text-red-500 text-xs tracking-wide mt-2">{{ errors.industry }}</span>
                    </div>

                    <a-checkbox-group v-model:value="form.industry" class="grid grid-cols-2 gap-3 mt-2">
                        <a-checkbox v-for="industry in industries" :key="industry" :value="industry" :disabled="(form.industry)?.length > 2 && !(form.industry).includes(industry)">{{ industry }}</a-checkbox>
                    </a-checkbox-group>
                </div>
            </div>
            <a-button @click="onSave" :loading="isLoading" size="large" type="primary" class="w-fit ms-auto">Save Change</a-button>
        </div>
    </layout-internal-container>
</template>

<script setup>
import { IconRosetteDiscountCheckFilled, IconUpload, IconMinus, IconPlus, IconExclamationCircleFilled } from "@tabler/icons-vue"
import { ref, onMounted, watch } from "vue"
import AuthApi from "@/api/auth"
import { message } from "ant-design-vue"
import useAuth from "@/composables/useAuth"
import { getLetters } from "@/plugins/utils"
import axios from "@/plugins/axios";

const yearOptions = Array.from({ length: 50 }, (_, i) => new Date().getFullYear() - i);
const beneficiaryOptions = [
    'Communities',
    'Cooperatives',
    'Social Enterprises',
    'Farmer Groups',
    'Women’s Groups',
    'Youth Organizations',
    'Others'
]

const authApi = new AuthApi()

const isLoading = ref(false)
const isVerifyPhoneNumber = ref(false)
const isLoadingResend = ref(false)
const otp = ref('')
const timer = ref(0)
const isTimerDisabled = ref(false)
const formattedTime = ref("05:00")
const isLoadingVerify = ref(false)
const provinceOptions = ref([]);
const fullName = ref('')

const form = ref({
    email: '',
    name: '',
    professional_link: '',
    gender: '',
    designation: '',
    position: '',
    company_sector: '',
    username: '',
    phone: '',
    description: '',
    company_name: '',
    year_estbl: '',
    industry: [],
    name: '',
    photo: '',
    province_id: '',
    beneficiaries_type: '',
    referral_source: '',
    other_hear: '',
    impact_measurement: '',
    funding_history: [],
})

const errors = ref({
    company_name: '',
    company_sector: '',
    position: '',
    professional_link: '',
    gender: '',
    designation: '',
    description: '',
    industry: '',
    year_estbl: '',
    name: '',
    email: '',
    phone: '',
    username: '',
    province_id: '',
    photo: '',
    beneficiaries_type: '',
    referral_source: '',
    other_hear: '',
    impact_measurement: '',
    funding_history: [
        // {
        //     year: '',
        //     investor: '',
        //     amount: '',
        //     id: Date.now(),
        // }
    ],
});


// Watchers for error handling
watch(form.value, (newValue) => {
    if (newValue.email) errors.value.email = '';
    if (newValue.name) errors.value.name = '';
    if (newValue.company_name) errors.value.company_name = '';
    if (newValue.company_sector) errors.value.company_sector = '';
    if (newValue.position) errors.value.position = '';
    if (newValue.professional_link) errors.value.professional_link = '';
    if (newValue.gender) errors.value.gender = '';
    if (newValue.designation) errors.value.designation = '';
    if (newValue.description) errors.value.description = '';
    if (newValue.industry) errors.value.industry = '';
    if (newValue.year_estbl) errors.value.year_estbl = '';
    if (newValue.province_id) errors.value.province_id = '';
    if (newValue.username) errors.value.username = '';
    if (newValue.phone) errors.value.phone = '';
    if (newValue.photo) errors.value.photo = '';
    if (newValue.beneficiaries_type) errors.value.beneficiaries_type = '';
    if (newValue.impact_measurement) errors.value.impact_measurement = '';
    if (newValue.referral_source) errors.value.referral_source = '';

    newValue.funding_history.forEach((item, index) => {
        if (newValue.funding_history[index]) errors.value.funding_history[index] = '';
    });

    isMounted.value = true
});

const sidebarKey = ref(null)

const photoFile = ref(null)
const isMounted = ref(false)

const industries = [
    "Agriculture", 
    "Arts, Culture & Entertainment", 
    "Education", 
    "Energy & Power", 
    "Environment & Waste Management", 
    "Food & Beverage", 
    "Financial Services", 
    "General Consultancy", 
    "Healthcare & Social Services", 
    "Housing & Accommodation", 
    "Information & Communications", 
    "Manufacturing", 
    "Materials & Packaging", 
    "Retail & Consumer Goods", 
    "Supply Chain, Logistics & Trading", 
    "Technology", 
    "Tourism Services", 
    "Transportation & Automotives",
    "Water Supply", 
    "Others"
]

const removeFunding = (item) => {
    const index = form.value.funding_history.indexOf(item);

    if (index !== -1) {
        form.value.funding_history.splice(index, 1);
        errors.value.funding_history.splice(index, 1);
    }
};
const addFunding = () => {
    form.value.funding_history.push({
        year: yearOptions[0],
        investor: '',
        amount: 0,
        id: Date.now(),
    });

    errors.value.funding_history.push({
        year: '',
        investor: '',
        amount: '',
        id: Date.now(),
    });
};

const onSave = async () => {
    isLoading.value = true
    // if (validateForm()) {
        try {
            let payload = {
                name: form.value.name,
                username: form.value.username,
                company_name: form.value.company_name,
                position: form.value.position,
                professional_link: form.value.professional_link,
                gender: form.value.gender,
                designation: form.value.designation,
                company_sector: form.value.company_sector,
                year_estbl: form.value.year_estbl,
                industry: typeof form.value.industry == 'string' ? JSON.parse(form.value.industry) : form.value.industry,
                description: form.value.description,
                province_id: form.value.province_id,
                beneficiaries_type: form.value.beneficiaries_type,
                referral_source: form.value.referral_source,
                other_hear: form.value.other_hear,
                impact_measurement: form.value.impact_measurement,
                funding_history: form.value.funding_history,
            }

            if (photoFile.value) {
                payload.photo = photoFile.value
            }
            const r = await authApi.updateProfile(payload);
    
            fetchProfile();
    
            message.success('Success Update Profile')
        } catch (error) {
            if (error.response && error.response.data && error.response.data.errors) {
                handleBackendErrors(error.response.data.errors);
            }

            message.error(error?.response?.data?.message);
        } finally {
            isLoading.value = false
        }
    // }
}

const fetchProfile = async () => {
    try {
        var r = await authApi.getProfile()
        if (!r.data) return
        Object.keys(r.data).forEach((key) => {
            if (key != 'funding_history') {
                form.value[key] = r.data[key] === null || r.data[key] === 'null' ? null : r.data[key]
            }
        })

        if (!r.data.funding_history) {
            form.value.funding_history = [
                {
                    year: yearOptions[0],
                    investor: '',
                    amount: 0,
                    id: Date.now(),
                }
            ]
            errors.value.funding_history = [
                {
                    year: '',
                    investor: '',
                    amount: '',
                    id: Date.now(),
                }
            ]
        }else{
            form.value.funding_history = [];
            const fundings = typeof r.data.funding_history == 'string' ? JSON.parse(r.data.funding_history) : r.data.funding_history;
            if (fundings) {
                fundings.forEach((item, index) => {
                    (form.value.funding_history).push({
                        year: item.year,
                        investor: item.investor,
                        amount: item.amount,
                        id: Date.now(),
                    });
                    
                    errors.value.funding_history.push('');
                })
            }
        }

        if (r.data.referral_source) {
            form.value.referral_source = ['Social media (Instagram, LinkedIn, Facebook, YouTube, etc.)', 'Instellar website', 'Instellar email newsletter', 'Friend/colleague'].includes(r.data.referral_source) ? r.data.referral_source : 'Other (write your own)';
            
            form.value.other_hear = !['Social media (Instagram, LinkedIn, Facebook, YouTube, etc.)', 'Instellar website', 'Instellar email newsletter', 'Friend/colleague'].includes(r.data.referral_source) ? r.data.referral_source : '';
        }

        form.value.industry = JSON.parse(r.data.industry)
        fullName.value = r.data.name

        sidebarKey.value = JSON.stringify(r.data)        

        useAuth().set(JSON.stringify(r.data), useAuth().get().token, true)
    } catch (error) {
        message.error(error?.response?.data?.message ?? 'Server Error')
    }
}

const startTimer = () => {
    isVerifyPhoneNumber.value = true

    timer.value = 300 // Set to 5 minutes (300 seconds)
    isTimerDisabled.value = true

    const interval = setInterval(() => {
        if (timer.value > 0) {
            timer.value -= 1
            formattedTime.value = formatTime(timer.value) // Update the displayed timer
        } else {
            isTimerDisabled.value = false
            clearInterval(interval)
        }
    }, 1000)
}

const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60).toString().padStart(2, '0')
    const secs = (seconds % 60).toString().padStart(2, '0')
    return `${minutes}:${secs}`
}

const onResend = async () => {
    if (!isTimerDisabled.value) { 
        isLoadingResend.value = true
        startTimer()
        try {
            await authApi.resendOtp()
        } catch (error) {
            message.error('halo')
        } 
        finally {
            isLoadingResend.value = false
        }
    }
}

const onVerify = async () => {
    if (otp.value) {
        isLoadingVerify.value = true
        try {
            const otpCode = otp.value
            await authApi.verifyOtp({ otp: otpCode })
            message.success("Successfully Verify WhatsApp!")
            fetchProfile()

            isVerifyPhoneNumber.value = true
        } catch (error) {
            message.error(error?.response?.data?.message ?? 'An error occurred. Please try again later.')
        } finally {
            isLoadingVerify.value = false
        }
    }
}

onMounted(() => {
    fetchProfile()
    fetchProvince()
})

const fileInput = ref(null);

const triggerFileUpload = () => {
    fileInput.value.click();
};

const fetchProvince = async () => {
    try {
        const response = await axios.get('/province');

        provinceOptions.value = response.data;
    } catch (error) {
        console.error('Error fetching province:', error)
    }
}

// Validate form data
// const validateForm = () => {
//     errors.value = {
//         company_name: '',
//         company_sector: '',
//         position: '',
//         professional_link: '',
//         designation: '',
//         industry: '',
//         year_estbl: '',
//         name: '',
//         email: '',
//         phone: '',
//         username: '',
//         province_id: '',
//         photo: ''
//     };

//     let isValid = true;

//     if (!form.value.company_sector) {
//         errors.value.company_sector = 'Company Sector is required.';
//         isValid = false;
//     }
//     if (!form.value.position) {
//         errors.value.position = 'Position is required.';
//         isValid = false;
//     }
//     if (!form.value.professional_link) {
//         errors.value.professional_link = 'Webiste or Social Media is required.';
//         isValid = false;
//     }
//     if (!form.value.designation) {
//         errors.value.designation = 'Designation is required.';
//         isValid = false;
//     }
//     if (form.value.industry.length == 0) {
//         errors.value.industry = 'Industry is required.';
//         isValid = false;
//     }
//     if (!form.value.description) {
//         errors.value.description = 'Description is required.';
//         isValid = false;
//     }
//     if (!form.value.year_estbl) {
//         errors.value.year_estbl = 'Year of Establishment is required.';
//         isValid = false;
//     }
//     if (!form.value.province_id) {
//         errors.value.province_id = 'Province is required.';
//         isValid = false;
//     }
//     if (!form.value.email) {
//         errors.value.email = 'Email is required.';
//         isValid = false;
//     }
//     if (!form.value.name) {
//         errors.value.name = 'Full name is required.';
//         isValid = false;
//     }
//     if (!form.value.company_name) {
//         errors.value.company_name = 'Company name is required.';
//         isValid = false;
//     }
//     if (!form.value.username) {
//         errors.value.username = 'Username is required.';
//         isValid = false;
//     }
//     if (!form.value.phone) {
//         errors.value.phone = 'Phone number is required.';
//         isValid = false;
//     }
//     if (!form.value.photo) {
//         errors.value.photo = 'Profil photo is required.';
//         isValid = false;
//     }
//     if (form.value.phone.startsWith('0')) {
//         errors.value.phone = 'Phone Format Invalid.';
//         isValid = false;
//     }

//     return isValid;
// };

// Handle backend errors
const handleBackendErrors = (backendErrors) => {
    Object.keys(backendErrors).forEach(field => {
        if (errors.value[field] !== undefined) {
            errors.value[field] = backendErrors[field][0];
        }else if(field.startsWith('funding_history.')){
            const fieldSplit = field.split('.');
            const index = fieldSplit[1];
            errors.value.funding_history[index] = backendErrors[field][0];
        }
    });
};

const handleFileChange = (event) => {
    const file = event.target.files[0];
    photoFile.value = file
    if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
            form.value.photo = e.target.result;
        };
        reader.readAsDataURL(file);
    }
};
</script>
